import type { Dispatch, ReactNode, SetStateAction } from 'react';
import React, { createContext, useContext, useState } from 'react';

import classNames from 'classnames';

import { Tab } from './Tab/Tab';
import { TabList } from './TabList/TabList';
import { TabPanel } from './TabPanel/TabPanel';
import { TabPanels } from './TabPanels/TabPanels';

type TabContext = {
  activeIndex: number;
  id: string;
  onChange: (args: { index: number }) => void;
  onSelectTab: Dispatch<SetStateAction<number>>;
};

const Context = createContext<undefined | TabContext>(undefined);

type TabsProps = {
  id?: string;
  children?: ReactNode;
  className?: string;
  defaultActiveIndex?: number;
  onChange?: (args: { index: number }) => void;
};

function Tabs({
  id = 'tabs',
  children,
  className,
  defaultActiveIndex = 0,
  onChange = () => {},
}: TabsProps) {
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
  const tabClassName = classNames('Tabs', className);

  return (
    <Context.Provider
      value={{
        activeIndex,
        id,
        onChange,
        onSelectTab: setActiveIndex,
      }}
    >
      <div className={tabClassName}>{children}</div>
    </Context.Provider>
  );
}

export const useTabs = () => {
  const tabsContext = useContext(Context);

  if (!tabsContext) {
    throw new Error(
      'No tabs context defined, make sure you are using Context.Provider',
    );
  }
  return tabsContext;
};

Tabs.Tab = Tab;
Tabs.TabList = TabList;
Tabs.TabPanel = TabPanel;
Tabs.TabPanels = TabPanels;

Tabs.displayName = 'Tabs';

export { Context, Tabs };
